import axios, { type AxiosError } from "axios";
import { hashKey, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useState, useSyncExternalStore } from "react";

class ReadRateLimitError extends Error {
  readonly response: AxiosError["response"];
  constructor(
    error: AxiosError,
    readonly retryAt: number
  ) {
    super(error.message);
    this.name = "ReadRateLimitError";
    this.response = error.response;
  }
}

interface QueryAccess {
  denied?: unknown;
  allowedEpoch: number;
}
interface ReadGate {
  epoch: number;
  limited?: ReadRateLimitError;
  denied?: unknown;
  lastDenial?: { epoch: number; error: unknown };
  queries?: Map<string, QueryAccess>;
}
const gates = new WeakMap<
  import("@tanstack/react-query").QueryClient,
  Map<string, ReadGate>
>();
const listeners = new WeakMap<
  import("@tanstack/react-query").QueryClient,
  Map<string, Set<() => void>>
>();
function publish(
  client: import("@tanstack/react-query").QueryClient,
  scope: string,
  state: ReadGate | undefined
) {
  const records = gates.get(client);
  if (state) records?.set(scope, state);
  else records?.delete(scope);
  for (const notify of listeners.get(client)?.get(scope) ?? []) notify();
}
export function readAccess(
  client: import("@tanstack/react-query").QueryClient,
  scope: QueryKey,
  queryKey: QueryKey
) {
  const state = gates.get(client)?.get(hashKey(scope));
  const own = state?.queries?.get(hashKey(queryKey));
  const beforeDenial =
    state?.lastDenial && (own?.allowedEpoch ?? 0) < state.lastDenial.epoch;
  return {
    denied:
      state?.denied ??
      own?.denied ??
      (beforeDenial ? state.lastDenial?.error : undefined),
    retryAt: state?.limited?.retryAt ?? 0,
  };
}
/** Guard actual read traffic, including invalidation/polling, across one resource's query variants. */
export function useQueryReadGuard(
  queryKey: QueryKey,
  resourceScope: QueryKey = queryKey
) {
  const client = useQueryClient();
  const scope = hashKey(resourceScope);
  const queryScope = hashKey(queryKey);
  useSyncExternalStore(
    (notify) => {
      let scopes = listeners.get(client);
      if (!scopes) {
        scopes = new Map();
        listeners.set(client, scopes);
      }
      let callbacks = scopes.get(scope);
      if (!callbacks) {
        callbacks = new Set();
        scopes.set(scope, callbacks);
      }
      callbacks.add(notify);
      return () => {
        callbacks.delete(notify);
        if (!callbacks.size) scopes.delete(scope);
      };
    },
    () => gates.get(client)?.get(scope)
  );
  return async <T>(read: () => Promise<T>): Promise<T> => {
    let records = gates.get(client);
    if (!records) {
      records = new Map();
      gates.set(client, records);
    }
    const atStart = records.get(scope);
    const startedEpoch = atStart?.epoch ?? 0;
    const owningQuery = client.getQueryCache().find({ queryKey, exact: true });
    const cachedError = owningQuery?.state.error;
    const limited =
      atStart?.limited ??
      (cachedError instanceof ReadRateLimitError ? cachedError : undefined);
    if (limited && Date.now() < limited.retryAt)
      throw (
        atStart?.queries?.get(queryScope)?.denied ?? atStart?.denied ?? limited
      );
    let result: T;
    try {
      result = await read();
    } catch (error) {
      const status = readFailureStatus(error);
      const current = records.get(scope);
      const epoch = (current?.epoch ?? 0) + 1;
      if (status === 429 && axios.isAxiosError(error)) {
        const rate = new ReadRateLimitError(
          error,
          Math.max(
            current?.limited?.retryAt ?? 0,
            readRetryAt(error, Date.now())
          )
        );
        publish(client, scope, { ...current, epoch, limited: rate });
        throw (
          current?.queries?.get(queryScope)?.denied ?? current?.denied ?? rate
        );
      }
      if (status && [401, 403, 404].includes(status)) {
        const queries = new Map(current?.queries);
        queries.set(queryScope, {
          allowedEpoch: queries.get(queryScope)?.allowedEpoch ?? 0,
          denied: error,
        });
        publish(client, scope, {
          ...current,
          epoch,
          denied: error,
          lastDenial: { epoch, error },
          queries,
        });
      }
      const latest = records.get(scope);
      throw latest?.queries?.get(queryScope)?.denied ?? latest?.denied ?? error;
    }
    const current = records.get(scope);
    if (current?.lastDenial && current.lastDenial.epoch > startedEpoch) {
      // Quarantine the pre-denial response itself, even after a sibling has recovered.
      // This is not a new server denial and must not advance/poison the recovery epoch.
      if (
        (current.queries?.get(queryScope)?.allowedEpoch ?? 0) <
        current.lastDenial.epoch
      ) {
        const queries = new Map(current.queries);
        queries.set(queryScope, {
          allowedEpoch: queries.get(queryScope)?.allowedEpoch ?? 0,
          denied: current.lastDenial.error,
        });
        publish(client, scope, { ...current, queries });
      }
      throw current.lastDenial.error;
    }
    if (current && owningQuery?.state.fetchStatus === "fetching") {
      // Publish authorization only after this query commits its payload. A
      // transport-level release can expose its older cached data for one render.
      const unsubscribe = client.getQueryCache().subscribe((event) => {
        if (event.query !== owningQuery) return;
        if (event.type === "removed") {
          unsubscribe();
          return;
        }
        if (event.type !== "updated") return;
        if (event.action.type === "success" && !event.action.manual) {
          unsubscribe();
          const latest = records.get(scope);
          if (
            !latest ||
            (latest.lastDenial && latest.lastDenial.epoch > startedEpoch)
          )
            return;
          const queries = new Map(latest.queries);
          queries.set(queryScope, {
            allowedEpoch: Math.max(
              queries.get(queryScope)?.allowedEpoch ?? 0,
              startedEpoch
            ),
          });
          // Keep the decision tombstone, and do not erase a newer cooldown.
          publish(client, scope, {
            ...latest,
            denied: undefined,
            queries,
            limited: latest.epoch === startedEpoch ? undefined : latest.limited,
          });
        } else if (event.query.state.fetchStatus === "idle") unsubscribe();
      });
    }
    return result;
  };
}
export function readFailureStatus(error: unknown): number | undefined {
  if (error instanceof ReadRateLimitError) return 429;
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}
export function classifyReadFailure(error: unknown) {
  switch (readFailureStatus(error)) {
    case 401:
      return "unauthenticated";
    case 403:
      return "forbidden";
    case 404:
      return "missing";
    case 429:
      return "rate-limited";
    default:
      return "unavailable";
  }
}

export function readRetryAt(error: unknown, failedAt: number): number {
  if (error instanceof ReadRateLimitError) return error.retryAt;
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return 0;
  const value = error.response.headers?.["retry-after"];
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    const deadline = failedAt + Number(value) * 1000;
    if (Number.isSafeInteger(deadline)) return deadline;
  }
  const isHttpDate =
    typeof value === "string" &&
    /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(value);
  const date = isHttpDate ? Date.parse(value) : NaN;
  return Number.isFinite(date) ? date : failedAt + 60_000;
}
const canRefresh = (query: {
  state: { error: unknown; errorUpdatedAt: number };
}) => Date.now() >= readRetryAt(query.state.error, query.state.errorUpdatedAt);
/** Query-only options; business mutations retain their own retry/reconciliation rules. */
export const readPolicy = {
  retry: (count: number, error: unknown) => {
    const status = readFailureStatus(error);
    return !(status && status >= 400 && status < 500) && count < 2;
  },
  retryDelay: (count: number) => Math.min(1000 * 2 ** count, 30_000),
  refetchOnWindowFocus: canRefresh,
  refetchOnReconnect: canRefresh,
  refetchOnMount: canRefresh,
  retryOnMount: false,
};

/** One wake-up at the deadline; no per-second countdown or mutation retries. */
export function useReadRecovery(
  error: unknown,
  errorUpdatedAt: number,
  resourceRetryAt = 0
) {
  const retryAt = Math.max(resourceRetryAt, readRetryAt(error, errorUpdatedAt));
  const [, wake] = useState(0);
  useEffect(() => {
    const remaining = retryAt - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => wake((value) => value + 1), remaining + 1);
    return () => clearTimeout(timer);
  }, [retryAt]);
  return { isCoolingDown: retryAt > Date.now(), retryAt };
}
